/**
 * To-Do List Process
 * 
 * Implements a simple to-do list: single column view with tasks.
 * AI can spawn multiple to-do list views if needed for different categories.
 * 
 * **Layout Anatomy:**
 * - Single column view with a list of tasks
 * - Each task can be checked off
 * - Tasks can be added/removed dynamically
 * 
 * **Characteristics:**
 * - Priority: NORMAL
 * - Lifecycle: STOP_ON_ALL_VIEWS_CLOSE
 * - Views: 1 view minimum (AI can spawn multiple for different categories)
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import { 
  designTokens, 
  createHeading, 
  createListItem, 
  createScrollableContainer,
  createEmptyState,
  createContainer,
  createTextInput,
  createButton
} from '../../core/design-system.js';
import { renderListItems, type ListItemConfig } from '../../core/list-item-renderer.js';
import { SortPresets } from '../../core/item-sorter.js';
import { DataLoader } from '../../core/data-loader.js';
import type { Todo } from '../../types/todo.js';
import { createTodo, updateTodo, completeTodo, uncompleteTodo, deleteTodo as removeTodo } from '../../types/todo.js';
import { TodoStorage } from '../../services/storage/local-storage.js';
import { cortexBridge } from '../../services/cortex-bridge/service.js';

interface TodoListState {
  title: string;
  todos: Todo[];
  category?: string;
}

export class TodoListProcess extends BaseProcess {
  kind: string = 'todo-list';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'productivity' as const,
    canStop: true,
    preserveState: true,
    timeSensitive: false
  };

  private state: TodoListState = {
    title: 'To-Do List',
    todos: []
  };

  private todoListView: TodoListView | null = null;

  constructor(id: string) {
    super(id);
    // Load todos from localStorage
    this.loadTodos();
    
    // Listen for remote updates
    bus.on('todo.added.remote', (todo: any) => {
      // Avoid duplicate if we already have it (from optimistic update)
      if (!this.state.todos.some(t => t.id === todo.id)) {
        this.state.todos.push(todo);
        TodoStorage.add(todo);
        this.updateState(this.state);
      }
    });
  }

  private loadTodos(): void {
    const todos = TodoStorage.getAll();
    // Filter by category if this process has one
    this.state.todos = todos;
    if (this.state.category) {
      this.state.todos = todos.filter(t => t.category === this.state.category);
    }
  }

  private saveTodos(): void {
    // Save all todos (not just filtered ones)
    const allTodos = TodoStorage.getAll();
    const otherTodos = allTodos.filter(t => 
      !this.state.todos.some(st => st.id === t.id)
    );
    TodoStorage.save([...otherTodos, ...this.state.todos]);
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[TodoListProcess] onStart called for process ${this.id}`);
    
    // Create single view with to-do list
    const view = new TodoListView(
      this.id,
      `${this.id}-main`,
      this.state,
      this
    );
    const viewInstance = view.toViewInstance();
    this.todoListView = view;
    
    log.debug(`[TodoListProcess] Registering view: ${viewInstance.id}`);
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);

    // Emit process.started
    log.debug(`[TodoListProcess] View registered, emitting process.started`);
    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  updateState(newState: TodoListState): void {
    this.state = newState;
    this.saveTodos();
    // Update the view
    if (this.todoListView) {
      this.todoListView.updateState(this.state);
    }
  }

  /**
   * Add a new todo
   */
  addTodo(title: string, options: {
    description?: string;
    due_date?: number;
    priority?: 'low' | 'medium' | 'high';
  } = {}): void {
    const todo = createTodo(title, {
      ...options,
      category: this.state.category
    });
    this.state.todos.push(todo);
    TodoStorage.add(todo);
    this.updateState(this.state);
    
    bus.emit('todo.added', {
      processId: this.id,
      todoId: todo.id,
      todo
    });

    // Sync to Cortex
    cortexBridge.sendIntent('ADD_TODO', todo);
  }

  /**
   * Update a todo
   */
  updateTodo(todoId: string, updates: Partial<Todo>): void {
    const index = this.state.todos.findIndex(t => t.id === todoId);
    if (index !== -1) {
      this.state.todos[index] = updateTodo(this.state.todos[index], updates);
      TodoStorage.update(todoId, this.state.todos[index]);
      this.updateState(this.state);
      
      bus.emit('todo.updated', {
        processId: this.id,
        todoId,
        todo: this.state.todos[index]
      });

      // Sync to Cortex
      cortexBridge.sendIntent('UPDATE_TODO', { id: todoId, updates });
    }
  }

  /**
   * Toggle todo completion
   */
  toggleTodo(todoId: string): void {
    const index = this.state.todos.findIndex(t => t.id === todoId);
    if (index !== -1) {
      const todo = this.state.todos[index];
      this.state.todos[index] = todo.status === 'completed' 
        ? uncompleteTodo(todo)
        : completeTodo(todo);
      TodoStorage.update(todoId, this.state.todos[index]);
      this.updateState(this.state);
      
      bus.emit('todo.toggled', {
        processId: this.id,
        todoId,
        completed: this.state.todos[index].status === 'completed'
      });

      // Sync to Cortex
      cortexBridge.sendIntent('UPDATE_TODO', { 
        id: todoId, 
        updates: { status: this.state.todos[index].status } 
      });
    }
  }

  /**
   * Delete a todo
   */
  deleteTodo(todoId: string): void {
    this.state.todos = removeTodo(this.state.todos, todoId);
    TodoStorage.delete(todoId);
    this.updateState(this.state);
    
    bus.emit('todo.deleted', {
      processId: this.id,
      todoId
    });

    // Sync to Cortex
    cortexBridge.sendIntent('DELETE_TODO', { id: todoId });
  }
}

/**
 * To-Do List View (HTML-based)
 * 
 * Renders a single column to-do list.
 */
class TodoListView extends BaseViewHTML {
  constructor(
    processId: string,
    viewId: string,
    private state: TodoListState,
    private process: TodoListProcess
  ) {
    super(processId, viewId, {
      acceptableSizes: [
        { cols: 2, rows: 6 }, // Preferred: tall and narrow
        { cols: 2, rows: 4 },  // Minimum: 2x4
        { cols: 3, rows: 5 }   // Alternative: 3x5
      ],
      showDismissButton: true,
      priority: 100
    });
  }

  protected getContentHTML(): string {
    const priorityColors = {
      low: 'rgba(0, 200, 0, 0.4)',
      medium: 'rgba(255, 200, 0, 0.4)',
      high: 'rgba(255, 0, 0, 0.4)'
    };
    
    const heading = createHeading(3, this.state.title);
    
    // Add new todo input
    const addInput = createTextInput({
      placeholder: 'Add a new task...',
      className: 'todo-add-input'
    });
    
    const addButton = createButton('Add', {
      className: 'todo-add-button',
      variant: 'primary'
    });
    
    const addSection = `
      <div style="
        display: flex;
        gap: ${designTokens.padding.sm};
        margin-bottom: ${designTokens.padding.md};
      ">
        ${addInput}
        ${addButton}
      </div>
    `;
    
    // Sort todos: incomplete first, then by priority, then by due date
    const sortedTodos = [...this.state.todos].sort((a, b) => {
      if (a.status === 'completed' && b.status !== 'completed') return 1;
      if (a.status !== 'completed' && b.status === 'completed') return -1;
      
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
      if (priorityDiff !== 0) return priorityDiff;
      
      if (a.due_date && b.due_date) {
        return a.due_date - b.due_date;
      }
      if (a.due_date) return -1;
      if (b.due_date) return 1;
      
      return a.created_at - b.created_at;
    });
    
    const itemsHTML = sortedTodos.length === 0
      ? createEmptyState('No tasks yet')
      : sortedTodos.map(todo => {
          const isCompleted = todo.status === 'completed';
          const itemContent = `
            <input 
              type="checkbox" 
              class="todo-checkbox" 
              data-todo-id="${todo.id}"
              ${isCompleted ? 'checked' : ''}
              style="
                margin-top: 2px;
                cursor: pointer;
                accent-color: ${designTokens.contentColor};
              "
            />
            <div 
              class="todo-text" 
              data-todo-id="${todo.id}"
              contenteditable="true"
              style="
                flex: 1;
                font-size: ${designTokens.fontSize.sm};
                color: ${designTokens.contentColor};
                text-decoration: ${isCompleted ? 'line-through' : 'none'};
                opacity: ${isCompleted ? '0.6' : '1'};
                cursor: text;
                outline: none;
                min-height: 20px;
              "
            >
              ${this.escapeHtml(todo.title)}
            </div>
            ${todo.priority ? `
              <div 
                class="todo-priority" 
                data-priority="${todo.priority}"
                style="
                  width: 12px;
                  height: 12px;
                  background: ${priorityColors[todo.priority] || 'rgba(255, 255, 255, 0.2)'};
                  border-radius: 2px;
                  flex-shrink: 0;
                  cursor: pointer;
                "
                title="Priority: ${todo.priority}"
              ></div>
            ` : ''}
            <button 
              class="todo-delete" 
              data-todo-id="${todo.id}"
              style="
                background: transparent;
                border: none;
                color: ${designTokens.contentColorMuted};
                cursor: pointer;
                padding: ${designTokens.padding.xs};
                font-size: ${designTokens.fontSize.xs};
                opacity: 0.5;
                transition: opacity 0.2s;
              "
              title="Delete task"
            >×</button>
          `;
          
          return createListItem(itemContent, {
            className: `todo-item ${isCompleted ? 'completed' : ''}`,
            style: `
              display: flex;
              align-items: flex-start;
              gap: ${designTokens.padding.sm};
              opacity: ${isCompleted ? '0.6' : '1'};
            `,
            variant: isCompleted ? 'subtle' : 'default'
          });
        }).join('');
    
    const scrollableContent = createScrollableContainer(
      `<ul style="list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: ${designTokens.padding.sm};">${itemsHTML}</ul>`,
      { direction: 'vertical' }
    );
    
    return createContainer(
      heading + addSection + scrollableContent,
      { padding: 'lg', flexDirection: 'column' }
    );
  }

  protected render(): void {
    // Call parent to set base styles
    super.render();
    
    if (!this.element) return;
    
    // Setup add todo functionality
    const addInput = this.element.querySelector('.todo-add-input') as HTMLInputElement;
    const addButton = this.element.querySelector('.todo-add-button') as HTMLButtonElement;
    
    const handleAdd = () => {
      if (addInput && addInput.value.trim()) {
        this.process.addTodo(addInput.value.trim());
        addInput.value = '';
      }
    };
    
    if (addButton) {
      addButton.addEventListener('click', handleAdd);
    }
    
    if (addInput) {
      addInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          handleAdd();
        }
      });
    }
    
    // Setup checkbox event listeners (toggle completion)
    const checkboxes = this.element.querySelectorAll('.todo-checkbox');
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        const todoId = target.getAttribute('data-todo-id');
        if (todoId) {
          this.process.toggleTodo(todoId);
        }
      });
    });
    
    // Setup text editing (inline edit)
    const todoTexts = this.element.querySelectorAll('.todo-text');
    todoTexts.forEach(textEl => {
      const todoId = textEl.getAttribute('data-todo-id');
      if (todoId) {
        textEl.addEventListener('blur', (e) => {
          const target = e.target as HTMLElement;
          const newTitle = target.innerText.trim();
          if (newTitle) {
            this.process.updateTodo(todoId, { title: newTitle });
          } else {
            // Restore original if empty
            const todo = this.state.todos.find(t => t.id === todoId);
            if (todo) {
              target.innerText = todo.title;
            }
          }
        });
        
        textEl.addEventListener('keypress', (e) => {
          const ke = e as KeyboardEvent;
          if (ke.key === 'Enter') {
            ke.preventDefault();
            (ke.target as HTMLElement).blur();
          }
        });
      }
    });
    
    // Setup delete buttons
    const deleteButtons = this.element.querySelectorAll('.todo-delete');
    deleteButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const todoId = button.getAttribute('data-todo-id');
        if (todoId && confirm('Delete this task?')) {
          this.process.deleteTodo(todoId);
        }
      });
      
      // Show delete button on hover
      const listItem = button.closest('.todo-item');
      if (listItem) {
        listItem.addEventListener('mouseenter', () => {
          (button as HTMLElement).style.opacity = '1';
        });
        listItem.addEventListener('mouseleave', () => {
          (button as HTMLElement).style.opacity = '0.5';
        });
      }
    });
  }

  updateState(newState: TodoListState): void {
    log.debug(`[TodoListView] updateState() called for ${this.id}`);
    this.state = newState;
    this.markDirty();
  }
}

registerProcess('todo-list', (id: string) => new TodoListProcess(id));

